import type { OpsLogisticDefinition } from '../types';

/**
 * Developer-owned logistics catalog.
 *
 * CRITICAL RULES:
 * - Ops/templates must reference logistics by `id` only (never by field paths).
 * - Firestore mappings live here (locked). Editing UI must not allow changing them.
 * - Every logistic has exactly one source of truth: school OR squad OR derived.
 */
const MARKETING_LOGISTICS_OUTCOME_OPTIONS = [
  { id: 'did_it', label: 'Did it' },
  { id: 'not_allowed', label: 'Not allowed' },
  { id: 'decided_not_to', label: 'Decided not to' },
  { id: 'not_enough_time', label: 'Not enough time' },
] as const;

const YES_NO_OPTIONS = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
] as const;

export const OPS_LOGISTICS_CATALOG: ReadonlyArray<OpsLogisticDefinition> = [
  // ------------------------
  // School-shared (canonical)
  // ------------------------
  {
    id: 'school.name',
    label: 'School name',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'name' },
    systemRequired: true,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.address',
    label: 'School address',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'address' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.organizationType',
    label: 'School type',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'organizationType' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'private_school', label: 'Private school' },
      { id: 'charter_school', label: 'Charter school' },
      { id: 'public_charter_school', label: 'Public charter school' },
      { id: 'traditional_public', label: 'Traditional public' },
      { id: 'public_magnet', label: 'Public magnet' },
      { id: 'community_organization', label: 'Community organization' },
      { id: 'other', label: 'Other' },
    ],
  },
  {
    id: 'school.gradeLevelsOffered',
    label: 'Grade levels offered',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'gradeLevelsOffered' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'pre_k', label: 'Pre-K' },
      { id: 'k_8', label: 'K-8' },
      { id: 'elementary', label: 'Elementary' },
      { id: 'middle', label: 'Middle' },
      { id: 'k_12', label: 'K-12' },
      { id: 'high', label: 'High' },
      { id: 'other', label: 'Other' },
    ],
  },
  // -------------------------
  // School billing (invoicing)
  // -------------------------
  {
    id: 'school.billingProfile.legalName',
    label: 'Billing: legal name',
    scope: 'school',
    type: 'text',
    storage: { kind: 'billing_profile', fieldPath: 'legalName' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.billingProfile.paymentPreference',
    label: 'Billing: payment preference',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'billing_profile', fieldPath: 'paymentPreference' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'ach', label: 'ACH' },
      { id: 'check', label: 'Check' },
      { id: 'card', label: 'Card' },
      { id: 'portal', label: 'Portal' },
      { id: 'other', label: 'Other' },
    ],
  },
  {
    id: 'school.billingProfile.netInvoiceDays',
    label: 'Billing: net invoice days',
    scope: 'school',
    type: 'number',
    storage: { kind: 'billing_profile', fieldPath: 'netInvoiceDays' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'minNumber', min: 0 },
    helpText: 'Days from invoice date until planned send date (0 = send immediately).',
  },
  {
    id: 'school.billingProfile.netPaymentDays',
    label: 'Billing: net payment days',
    scope: 'school',
    type: 'number',
    storage: { kind: 'billing_profile', fieldPath: 'netPaymentDays' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'minNumber', min: 0 },
    helpText: 'Days from invoice date until payment is due.',
  },
  {
    id: 'school.billingProfile.billingAddress.line1',
    label: 'Billing: address line 1',
    scope: 'school',
    type: 'text',
    storage: { kind: 'billing_profile', fieldPath: 'billingAddress.line1' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.billingProfile.billingAddress.line2',
    label: 'Billing: address line 2',
    scope: 'school',
    type: 'text',
    storage: { kind: 'billing_profile', fieldPath: 'billingAddress.line2' },
    systemRequired: false,
    gates: ['invoicing'],
    // Optional field; blank is valid and should not block checkpoint completion.
    completeness: { kind: 'custom', note: 'Optional — can be blank.' },
  },
  {
    id: 'school.billingProfile.billingAddress.city',
    label: 'Billing: city',
    scope: 'school',
    type: 'text',
    storage: { kind: 'billing_profile', fieldPath: 'billingAddress.city' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.billingProfile.billingAddress.state',
    label: 'Billing: state',
    scope: 'school',
    type: 'text',
    storage: { kind: 'billing_profile', fieldPath: 'billingAddress.state' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.billingProfile.billingAddress.postalCode',
    label: 'Billing: postal code',
    scope: 'school',
    type: 'text',
    storage: { kind: 'billing_profile', fieldPath: 'billingAddress.postalCode' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.billingProfile.billingAddress.country',
    label: 'Billing: country',
    scope: 'school',
    type: 'text',
    storage: { kind: 'billing_profile', fieldPath: 'billingAddress.country' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.bgChecksAndSafetyTrainingsCompleted',
    label: 'BG checks and safety learning completed',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'bgChecksAndSafetyTrainingsCompleted' },
    systemRequired: false,
    gates: ['teacher_assignment', 'other'],
    completeness: { kind: 'exists' },
    helpText: 'Manual flag: we do not currently track per-requirement completion state.',
  },
  // ------------------------------
  // Teacher assignment readiness
  // ------------------------------
  {
    id: 'school.teacherAssignRequirements.requiredPeopleCount',
    label: 'Teacher-assign: # people needing checks/learning',
    scope: 'school',
    type: 'number',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.requiredPeopleCount' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'minNumber', min: 1 },
    helpText: 'How many people must be cleared (e.g., 3).',
  },

  // Basic (Overture) background check
  {
    id: 'school.teacherAssignRequirements.basicBgCheck.requiredStatus',
    label: 'Teacher-assign: Basic BG check required?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.basicBgCheck.requiredStatus' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'unknown', label: 'Unknown' },
    ],
    helpText: 'Basic background check (we can send Overture BG check).',
  },
  {
    id: 'school.teacherAssignRequirements.basicBgCheck.estimatedDays',
    label: 'Teacher-assign: Basic BG check estimated days',
    scope: 'school',
    type: 'number',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.basicBgCheck.estimatedDays' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'minNumber', min: 0 },
  },
  {
    id: 'school.teacherAssignRequirements.basicBgCheck.notes',
    label: 'Teacher-assign: Basic BG check notes',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.basicBgCheck.notes' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
  },

  // School-specific background check
  {
    id: 'school.teacherAssignRequirements.schoolSpecificBgCheck.requiredStatus',
    label: 'Teacher-assign: School-specific BG check required?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.schoolSpecificBgCheck.requiredStatus' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'unknown', label: 'Unknown' },
    ],
  },
  {
    id: 'school.teacherAssignRequirements.schoolSpecificBgCheck.runner',
    label: 'Teacher-assign: School-specific BG check run by',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.schoolSpecificBgCheck.runner' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'school_runs', label: 'School runs it' },
      { id: 'district_portal', label: 'District portal' },
      { id: 'third_party_vendor', label: 'Third-party vendor' },
      { id: 'unknown', label: 'Unknown' },
    ],
  },
  {
    id: 'school.teacherAssignRequirements.schoolSpecificBgCheck.estimatedDays',
    label: 'Teacher-assign: School-specific BG check estimated days',
    scope: 'school',
    type: 'number',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.schoolSpecificBgCheck.estimatedDays' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'minNumber', min: 0 },
  },
  {
    id: 'school.teacherAssignRequirements.schoolSpecificBgCheck.notes',
    label: 'Teacher-assign: School-specific BG check notes',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.schoolSpecificBgCheck.notes' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
  },

  // Fingerprinting
  {
    id: 'school.teacherAssignRequirements.fingerprinting.requiredStatus',
    label: 'Teacher-assign: Fingerprinting required?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.fingerprinting.requiredStatus' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'unknown', label: 'Unknown' },
    ],
  },
  {
    id: 'school.teacherAssignRequirements.fingerprinting.runner',
    label: 'Teacher-assign: Fingerprinting scheduled by',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.fingerprinting.runner' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'overture', label: 'Overture' },
      { id: 'school', label: 'School' },
      { id: 'teacher', label: 'Teacher' },
      { id: 'unknown', label: 'Unknown' },
    ],
  },
  {
    id: 'school.teacherAssignRequirements.fingerprinting.estimatedDays',
    label: 'Teacher-assign: Fingerprinting estimated days',
    scope: 'school',
    type: 'number',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.fingerprinting.estimatedDays' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'minNumber', min: 0 },
  },
  {
    id: 'school.teacherAssignRequirements.fingerprinting.notes',
    label: 'Teacher-assign: Fingerprinting notes',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.fingerprinting.notes' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
  },

  // Mandatory reporting certification
  {
    id: 'school.teacherAssignRequirements.mandatoryReporting.requiredStatus',
    label: 'Teacher-assign: Mandatory reporting certification required?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.mandatoryReporting.requiredStatus' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'unknown', label: 'Unknown' },
    ],
  },
  {
    id: 'school.teacherAssignRequirements.mandatoryReporting.runner',
    label: 'Teacher-assign: Mandatory reporting provided by',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.mandatoryReporting.runner' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'school', label: 'School' },
      { id: 'district', label: 'District' },
      { id: 'state', label: 'State' },
      { id: 'overture', label: 'Overture' },
      { id: 'unknown', label: 'Unknown' },
    ],
  },
  {
    id: 'school.teacherAssignRequirements.mandatoryReporting.estimatedDays',
    label: 'Teacher-assign: Mandatory reporting estimated days',
    scope: 'school',
    type: 'number',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.mandatoryReporting.estimatedDays' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'minNumber', min: 0 },
  },
  {
    id: 'school.teacherAssignRequirements.mandatoryReporting.notes',
    label: 'Teacher-assign: Mandatory reporting notes',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.mandatoryReporting.notes' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
  },

  // Online training (general)
  {
    id: 'school.teacherAssignRequirements.onlineTraining.requiredStatus',
    label: 'Teacher-assign: Online learning required?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.onlineTraining.requiredStatus' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'unknown', label: 'Unknown' },
    ],
  },
  {
    id: 'school.teacherAssignRequirements.onlineTraining.runner',
    label: 'Teacher-assign: Online learning provided by',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.onlineTraining.runner' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'school', label: 'School' },
      { id: 'district', label: 'District' },
      { id: 'overture', label: 'Overture' },
      { id: 'third_party', label: 'Third-party' },
      { id: 'unknown', label: 'Unknown' },
    ],
  },
  {
    id: 'school.teacherAssignRequirements.onlineTraining.estimatedDays',
    label: 'Teacher-assign: Online learning estimated days',
    scope: 'school',
    type: 'number',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.onlineTraining.estimatedDays' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'minNumber', min: 0 },
  },
  {
    id: 'school.teacherAssignRequirements.onlineTraining.notes',
    label: 'Teacher-assign: Online learning notes',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'teacherAssignRequirements.onlineTraining.notes' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
  },
  // ------------------------
  // School screening (sales)
  // ------------------------
  {
    id: 'school.screening.hasPartnersLikeUs',
    label: 'Screening: has partners/vendors like us?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.hasPartnersLikeUs' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'unsure', label: 'Unsure' },
    ],
    helpText: 'Do they already work with enrichment vendors/program partners (similar to Overture)?',
  },
  {
    id: 'school.screening.partnerVendors',
    label: 'Screening: existing vendors / partner programs',
    scope: 'school',
    type: 'string_list',
    storage: { kind: 'schools', fieldPath: 'screening.partnerVendors' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
    helpText: 'One vendor/program per line (or leave blank).',
  },
  {
    id: 'school.screening.avgHouseholdIncomeUsd',
    label: 'Screening: average household income (USD)',
    scope: 'school',
    type: 'number',
    storage: { kind: 'schools', fieldPath: 'screening.avgHouseholdIncomeUsd' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'minNumber', min: 1 },
    helpText: 'Best estimate, in USD (e.g., 120000).',
  },
  {
    id: 'school.screening.householdIncomeBand',
    label: 'Screening: household income band',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.householdIncomeBand' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'lt_50k', label: '< $50k' },
      { id: '50_100k', label: '$50k–$100k' },
      { id: '100_150k', label: '$100k–$150k' },
      { id: '150k_plus', label: '$150k+' },
      { id: 'unknown', label: 'Unknown' },
    ],
    helpText: 'Use when exact average is unknown; helpful for quick segmentation.',
  },
  {
    id: 'school.screening.targetGradeLevels',
    label: 'Screening: grade levels for our program (target)',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.targetGradeLevels' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'pre_k', label: 'Pre-K' },
      { id: 'k_2', label: 'K–2' },
      { id: 'k_5', label: 'K–5' },
      { id: '3_5', label: '3–5' },
      { id: '6_8', label: '6–8' },
      { id: '9_12', label: '9–12' },
      { id: 'k_8', label: 'K–8' },
      { id: 'k_12', label: 'K–12' },
      { id: 'unknown', label: 'Unknown' },
    ],
    helpText: 'What grade band are they considering for Overture (may differ from the school’s overall grade levels)?',
  },
  {
    id: 'school.screening.openToMultiplePrograms',
    label: 'Screening: open to multiple programs?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.openToMultiplePrograms' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'maybe', label: 'Maybe' },
    ],
  },
  {
    id: 'school.screening.internalTeachersInterested',
    label: 'Screening: teachers interested in teaching / leading?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.internalTeachersInterested' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'unknown', label: 'Unknown' },
    ],
    helpText: 'Any teachers/staff who might want to run the program (or co-lead)? Capture names below.',
  },
  {
    id: 'school.screening.teacherLeadsNotes',
    label: 'Screening: teacher/staff leads (names + notes)',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'screening.teacherLeadsNotes' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Names, roles, availability, and any context.',
  },
  {
    id: 'school.screening.seasonalCamps',
    label: 'Screening: winter/summer camps?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.seasonalCamps' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'maybe', label: 'Maybe' },
    ],
    helpText: 'Do they run seasonal camps (winter break / summer) that we could support?',
  },
  {
    id: 'school.screening.mostPopularProgramDay',
    label: 'Screening: most popular current program day/time',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'screening.mostPopularProgramDay' },
    systemRequired: false,
    gates: ['scheduling', 'other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Example: "Wednesdays after school (3–4pm) is highest demand."',
  },
  {
    id: 'school.screening.openToAcademyOrDemo',
    label: 'Screening: open to an academy / demo?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.openToAcademyOrDemo' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'maybe', label: 'Maybe' },
    ],
    helpText: 'Would they want an on-site demo, assembly, or short pilot before committing?',
  },
  {
    id: 'school.screening.allowsOurPaymentSystem',
    label: 'Screening: can we run our own payment system?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.allowsOurPaymentSystem' },
    systemRequired: false,
    gates: ['invoicing', 'other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'maybe', label: 'Maybe' },
    ],
    helpText: 'Can parents pay through Overture (Stripe), or must payments run through the school/district?',
  },
  {
    id: 'school.screening.parentAccountsAllowed',
    label: 'Screening: can we create parent accounts?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.parentAccountsAllowed' },
    systemRequired: false,
    gates: ['parent_comms', 'other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'maybe', label: 'Maybe' },
    ],
  },
  {
    id: 'school.screening.parentEmailsAllowed',
    label: 'Screening: can we send parent emails?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.parentEmailsAllowed' },
    systemRequired: false,
    gates: ['parent_comms', 'other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'maybe', label: 'Maybe' },
    ],
  },
  {
    id: 'school.screening.ifNoCanSchoolSendAccountCreationEmail',
    label: 'Screening: if not, can the school send account creation emails?',
    scope: 'school',
    type: 'enum',
    storage: { kind: 'schools', fieldPath: 'screening.ifNoCanSchoolSendAccountCreationEmail' },
    systemRequired: false,
    gates: ['parent_comms', 'other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'unknown', label: 'Unknown' },
    ],
    helpText: 'Fallback path if we can’t email parents directly.',
  },
  {
    id: 'school.screening.parentCommsNotes',
    label: 'Screening: parent comms / data sharing notes',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'screening.parentCommsNotes' },
    systemRequired: false,
    gates: ['parent_comms', 'other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Anything about roster access, email list rules, consent requirements, district policies, etc.',
  },
  {
    id: 'school.screening.decisionProcess',
    label: 'Screening: decision process (who + steps)',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'screening.decisionProcess' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Who decides, who influences, and what approvals are required?',
  },
  {
    id: 'school.screening.fundingSource',
    label: 'Screening: funding source / budget owner',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'screening.fundingSource' },
    systemRequired: false,
    gates: ['invoicing', 'other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Examples: PTA, afterschool budget, grants, parents pay, district budget.',
  },
  // ------------------------
  // Lead outreach (sales)
  // ------------------------
  {
    id: 'school.leadOutreach.firstEmailSent',
    label: '1st email sent',
    scope: 'school',
    type: 'contact_date',
    storage: { kind: 'schools', fieldPath: 'leadOutreach.firstEmailSent' },
    systemRequired: false,
    gates: ['other'],
    allowNotApplicable: true,
    completeness: { kind: 'custom', note: 'Complete when both contact and date are set.' },
  },
  {
    id: 'school.leadOutreach.secondEmailSent',
    label: '2nd email sent',
    scope: 'school',
    type: 'contact_date',
    storage: { kind: 'schools', fieldPath: 'leadOutreach.secondEmailSent' },
    systemRequired: false,
    gates: ['other'],
    allowNotApplicable: true,
    completeness: { kind: 'custom', note: 'Complete when both contact and date are set.' },
  },
  {
    id: 'school.leadOutreach.thirdEmailSent',
    label: '3rd email sent',
    scope: 'school',
    type: 'contact_date',
    storage: { kind: 'schools', fieldPath: 'leadOutreach.thirdEmailSent' },
    systemRequired: false,
    gates: ['other'],
    allowNotApplicable: true,
    completeness: { kind: 'custom', note: 'Complete when both contact and date are set.' },
  },
  {
    id: 'school.leadOutreach.respondedToEmail',
    label: 'Responded to email',
    scope: 'school',
    type: 'contact_date',
    storage: { kind: 'schools', fieldPath: 'leadOutreach.respondedToEmail' },
    systemRequired: false,
    gates: ['other'],
    allowNotApplicable: true,
    completeness: { kind: 'custom', note: 'Complete when both contact and date are set.' },
  },
  {
    id: 'school.leadOutreach.callSetup',
    label: 'Call setup',
    scope: 'school',
    type: 'contact_date',
    storage: { kind: 'schools', fieldPath: 'leadOutreach.callSetup' },
    systemRequired: false,
    gates: ['other'],
    allowNotApplicable: true,
    completeness: { kind: 'custom', note: 'Complete when both contact and date are set.' },
  },
  {
    id: 'school.leadOutreach.coldCallMade',
    label: 'Cold Call made',
    scope: 'school',
    type: 'contact_date',
    storage: { kind: 'schools', fieldPath: 'leadOutreach.coldCallMade' },
    systemRequired: false,
    gates: ['other'],
    allowNotApplicable: true,
    completeness: { kind: 'custom', note: 'Complete when both contact and date are set.' },
  },
  {
    id: 'school.leadOutreach.inPersonVisit',
    label: 'In-person visit',
    scope: 'school',
    type: 'contact_date',
    storage: { kind: 'schools', fieldPath: 'leadOutreach.inPersonVisit' },
    systemRequired: false,
    gates: ['other'],
    allowNotApplicable: true,
    completeness: { kind: 'custom', note: 'Complete when both contact and date are set.' },
  },
  {
    id: 'school.leadOutreach.linkedinMessageSent',
    label: 'Linkedin message sent',
    scope: 'school',
    type: 'contact_date',
    storage: { kind: 'schools', fieldPath: 'leadOutreach.linkedinMessageSent' },
    systemRequired: false,
    gates: ['other'],
    allowNotApplicable: true,
    completeness: { kind: 'custom', note: 'Complete when both contact and date are set.' },
  },
  {
    id: 'school.vendorProcess.notes',
    label: 'District vendor process notes / status',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.notes' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Track whether vendor info was sent + instructions/steps (school-specific).',
  },
  {
    id: 'school.vendorProcess.registerForAccount',
    label: 'Vendor process: register for account',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.registerForAccount' },
    systemRequired: false,
    gates: ['other'],
    // Treat both Yes/No as "answered" (only blank is missing).
    completeness: { kind: 'exists' },
  },
  {
    id: 'school.coiRequired',
    label: 'COI required',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.requestAdditionalInsuranceCertificate' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
    helpText: 'Certificate of Insurance required by the school/district.',
  },
  {
    id: 'school.coiSent',
    label: 'COI sent',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.uploadInsuranceCertificate' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
    helpText: 'We have sent/uploaded the COI to the school/district.',
  },
  {
    id: 'school.coiNotes',
    label: 'COI notes',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.coiNotes' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Any special COI requirements (additional insured language, limits, deadlines, portal instructions, etc.).',
  },
  {
    id: 'school.vendorProcess.requestAdditionalInsuranceCertificate',
    label: 'Vendor process: request additional insurance certificate',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.requestAdditionalInsuranceCertificate' },
    systemRequired: false,
    gates: ['other'],
    // Treat both Yes/No as "answered" (only blank is missing).
    completeness: { kind: 'exists' },
  },
  {
    id: 'school.vendorProcess.uploadInsuranceCertificate',
    label: 'Vendor process: upload insurance certificate',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.uploadInsuranceCertificate' },
    systemRequired: false,
    gates: ['other'],
    // Treat both Yes/No as "answered" (only blank is missing).
    completeness: { kind: 'exists' },
  },
  {
    id: 'school.vendorProcess.uploadW9',
    label: 'Vendor process: upload W9',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.uploadW9' },
    systemRequired: false,
    gates: ['other'],
    // Treat both Yes/No as "answered" (only blank is missing).
    completeness: { kind: 'exists' },
  },
  {
    id: 'school.vendorProcess.signDistrictRoomRentalAgreement',
    label: 'Vendor process: sign district room rental agreement',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.signDistrictRoomRentalAgreement' },
    systemRequired: false,
    gates: ['other'],
    // Treat both Yes/No as "answered" (only blank is missing).
    completeness: { kind: 'exists' },
  },
  {
    id: 'school.vendorProcess.signDistrictVendorAgreement',
    label: 'Vendor process: sign district vendor agreement',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.signDistrictVendorAgreement' },
    systemRequired: false,
    gates: ['other'],
    // Treat both Yes/No as "answered" (only blank is missing).
    completeness: { kind: 'exists' },
  },
  {
    id: 'school.vendorProcess.submitSchoolRoomRentalRequest',
    label: 'Vendor process: submit school room rental request',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.submitSchoolRoomRentalRequest' },
    systemRequired: false,
    gates: ['other'],
    // Treat both Yes/No as "answered" (only blank is missing).
    completeness: { kind: 'exists' },
  },
  {
    id: 'school.vendorProcess.agreeToPayRoomRentalFee',
    label: 'Vendor process: agree to pay room rental fee',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'vendorProcess.agreeToPayRoomRentalFee' },
    systemRequired: false,
    gates: ['other'],
    // Treat both Yes/No as "answered" (only blank is missing).
    completeness: { kind: 'exists' },
  },
  {
    id: 'school.wifiUsername',
    label: 'WiFi username',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'wifiUsername' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.wifiPassword',
    label: 'WiFi password',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'wifiPassword' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.wifiRules',
    label: 'Additional WiFi rules',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'wifiRules' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.parkingInstructions',
    label: 'Parking instructions',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'parkingInstructions' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Where to park, how to access the building, where to check in, etc.',
  },
  {
    id: 'school.sitesWhitelisted',
    label: 'Our sites whitelisted (IT)',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'sitesWhitelisted' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'school.arrivalDismissalProcedure',
    label: 'Arrival / dismissal procedure',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'arrivalDismissalProcedure' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.arrivalProcedure',
    label: 'Arrival procedure',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'arrivalProcedure' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.dismissalProcedure',
    label: 'Dismissal procedure',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'dismissalProcedure' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.lateArrivalProcedure',
    label: 'Late arrival procedure',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'lateArrivalProcedure' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.bathroomRules',
    label: 'Bathroom rules',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'bathroomRules' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.hallwayRules',
    label: 'Hallway rules',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'hallwayRules' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.pocOnSite',
    label: 'Primary on-site point of contact (name)',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'pocOnSite' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.pocContactId',
    label: 'Primary on-site point of contact (contact)',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'pocContactId' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Historical field containing a school contact id. Canonical on-site contacts live under the partner and may be scoped by location.',
  },
  {
    id: 'school.emergencyContactOnSite',
    label: 'Main emergency contact on-site while teaching',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'emergencyContactOnSite' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.pocContactInfo',
    label: 'Primary on-site point of contact (email/phone)',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'pocContactInfo' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.registrationLink',
    label: 'Registration link',
    scope: 'school',
    type: 'url',
    storage: { kind: 'schools', fieldPath: 'registrationLink' },
    systemRequired: false,
    gates: ['marketing', 'parent_comms'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.logo',
    label: 'School logo (image)',
    scope: 'school',
    type: 'file',
    storage: { kind: 'schools', fieldPath: 'logo' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    ui: { kind: 'file', accept: 'image/*', storagePathPrefix: 'schools/{schoolId}/assets' },
  },
  {
    id: 'school.campusPhoto',
    label: 'Campus photo (image)',
    scope: 'school',
    type: 'file',
    storage: { kind: 'schools', fieldPath: 'campusPhoto' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    ui: { kind: 'file', accept: 'image/*', storagePathPrefix: 'schools/{schoolId}/assets' },
  },
  {
    id: 'school.safetyProtocolDocs',
    label: 'Safety protocol documents',
    scope: 'school',
    type: 'file_list',
    storage: { kind: 'schools', fieldPath: 'safetyProtocolDocs' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
    ui: { kind: 'file', multiple: true, accept: 'application/pdf,image/*', storagePathPrefix: 'schools/{schoolId}/safetyDocs' },
  },
  {
    id: 'school.reliabilityScore',
    label: 'School reliability score (1-5)',
    scope: 'school',
    type: 'number',
    storage: { kind: 'schools', fieldPath: 'reliabilityScore' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'minNumber', min: 1 },
    helpText: 'Subjective 1-5 score.',
  },
  {
    id: 'school.historyNotes',
    label: 'Notes on past interactions & history',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'historyNotes' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.attendanceSoftware',
    label: 'School attendance software',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'attendanceSoftware' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Example: PowerSchool, Infinite Campus, Schoology, Aspen, etc.',
  },
  {
    id: 'school.additionalNotes',
    label: 'Additional notes',
    scope: 'school',
    type: 'text',
    storage: { kind: 'schools', fieldPath: 'additionalNotes' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'school.additionalPointsOfContact',
    label: 'Additional points of contact',
    scope: 'school',
    type: 'string_list',
    storage: { kind: 'schools', fieldPath: 'additionalPointsOfContact' },
    systemRequired: false,
    gates: ['other', 'parent_comms'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'One per line. Format suggestion: "Name — role — email/phone".',
  },
  {
    id: 'school.firstDayInstructionsSent',
    label: 'First-day instructions sent',
    scope: 'school',
    type: 'boolean',
    storage: { kind: 'schools', fieldPath: 'firstDayInstructionsSent' },
    systemRequired: false,
    gates: ['parent_comms', 'other'],
    completeness: { kind: 'exists' },
  },

  // -----------------------
  // Squad-specific canonical
  // -----------------------
  {
    id: 'squad.timezone',
    label: 'Timezone',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'timezone' },
    systemRequired: true,
    gates: ['scheduling', 'teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'America/New_York', label: 'Eastern — America/New_York' },
      { id: 'America/Chicago', label: 'Central — America/Chicago' },
      { id: 'America/Denver', label: 'Mountain — America/Denver' },
      { id: 'America/Phoenix', label: 'Arizona — America/Phoenix' },
      { id: 'America/Los_Angeles', label: 'Pacific — America/Los_Angeles' },
      { id: 'America/Anchorage', label: 'Alaska — America/Anchorage' },
      { id: 'Pacific/Honolulu', label: 'Hawaii — Pacific/Honolulu' },
      { id: 'UTC', label: 'UTC' },
    ],
  },
  {
    id: 'squad.deliveryMode',
    label: 'Squad format (in-person vs online)',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'deliveryMode' },
    systemRequired: false,
    gates: ['scheduling', 'other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'in_person', label: 'In-person' },
      { id: 'online', label: 'Online' },
    ],
  },
  {
    id: 'squad.schedule.exceptions',
    label: 'No class days',
    scope: 'squad',
    type: 'date_list',
    storage: { kind: 'squads', fieldPath: 'schedule.exceptions' },
    systemRequired: false,
    gates: ['scheduling'],
    completeness: { kind: 'exists' },
    helpText: 'Add one or more no-class dates.',
  },
  {
    id: 'squad.schedule.makeups',
    label: 'Make up days',
    scope: 'squad',
    type: 'date_list',
    storage: { kind: 'squads', fieldPath: 'schedule.makeups' },
    systemRequired: false,
    gates: ['scheduling'],
    completeness: { kind: 'exists' },
    helpText: 'Add one or more make-up dates.',
  },
  {
    id: 'squad.schedule.dayOfWeek',
    label: 'Day of week',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'schedule.dayOfWeek' },
    systemRequired: true,
    gates: ['scheduling'],
    completeness: { kind: 'nonEmpty' },
    // Store legacy 3-letter codes to match existing squad schedule UI.
    enumOptions: [
      { id: 'MON', label: 'Monday' },
      { id: 'TUE', label: 'Tuesday' },
      { id: 'WED', label: 'Wednesday' },
      { id: 'THU', label: 'Thursday' },
      { id: 'FRI', label: 'Friday' },
      { id: 'SAT', label: 'Saturday' },
      { id: 'SUN', label: 'Sunday' },
    ],
  },
  {
    id: 'squad.schedule.startTime',
    label: 'Start time',
    scope: 'squad',
    type: 'time',
    storage: { kind: 'squads', fieldPath: 'schedule.startTime' },
    systemRequired: true,
    gates: ['scheduling'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.schedule.endTime',
    label: 'End time',
    scope: 'squad',
    type: 'time',
    storage: { kind: 'squads', fieldPath: 'schedule.endTime' },
    systemRequired: true,
    gates: ['scheduling'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.schedule.startDate',
    label: 'Start date',
    scope: 'squad',
    type: 'date',
    storage: { kind: 'squads', fieldPath: 'schedule.startDate' },
    systemRequired: true,
    gates: ['scheduling', 'invoicing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.schedule.endDate',
    label: 'End date',
    scope: 'squad',
    type: 'date',
    storage: { kind: 'squads', fieldPath: 'schedule.endDate' },
    systemRequired: true,
    gates: ['scheduling', 'invoicing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.roughDateWindow',
    label: 'Rough date window',
    scope: 'squad',
    type: 'text',
    storage: { kind: 'squads', fieldPath: 'roughDateWindow' },
    systemRequired: false,
    gates: ['scheduling'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Example: "Sept 9 – Nov 18"',
  },
  {
    id: 'squad.paymentMode',
    label: 'Payment mode',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'paymentMode' },
    systemRequired: true,
    gates: ['invoicing', 'marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'stripe', label: 'Stripe (parents pay online)' },
      { id: 'school_enrollment', label: 'School paid (enrollment-based)' },
      { id: 'school_flat_fee', label: 'School paid (flat fee)' },
      { id: 'external', label: 'External (legacy)' },
    ],
  },
  {
    id: 'squad.priceCents',
    label: 'Price (legacy)',
    scope: 'squad',
    type: 'number',
    storage: { kind: 'squads', fieldPath: 'priceCents' },
    systemRequired: false,
    gates: ['invoicing', 'marketing'],
    completeness: { kind: 'minNumber', min: 1 },
    helpText: 'Legacy field. Prefer tuitionPerStudentPerClassCents or flatFeePerClassCents.',
  },
  {
    id: 'squad.tuitionPerStudentPerClassCents',
    label: 'Tuition (per student per hour)',
    scope: 'squad',
    type: 'number',
    storage: { kind: 'squads', fieldPath: 'tuitionPerStudentPerClassCents' },
    systemRequired: false,
    gates: ['invoicing', 'marketing'],
    completeness: { kind: 'minNumber', min: 1 },
    helpText: 'Stored in cents. Used for Stripe + enrollment-based school billing as an hourly rate.',
  },
  {
    id: 'squad.flatFeePerClassCents',
    label: 'Flat fee (per class)',
    scope: 'squad',
    type: 'number',
    storage: { kind: 'squads', fieldPath: 'flatFeePerClassCents' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'minNumber', min: 1 },
    helpText: 'Stored in cents. Used for flat-fee school billing.',
  },
  {
    id: 'squad.registrationStartDate',
    label: 'Registration start date',
    scope: 'squad',
    type: 'date',
    storage: { kind: 'squads', fieldPath: 'registrationStartDate' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.registrationEndDate',
    label: 'Registration end date',
    scope: 'squad',
    type: 'date',
    storage: { kind: 'squads', fieldPath: 'registrationEndDate' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
  },
  // -------------------------
  // Marketing logistics (squad)
  // -------------------------
  {
    id: 'squad.marketingLogistics.reachedOutToDirectorOrMarketing',
    label: 'Reached out to director / marketing department',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.reachedOutToDirectorOrMarketing' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: YES_NO_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.agreedToDemo',
    label: 'Agreed to demo',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.agreedToDemo' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: YES_NO_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.agreedToFlyers',
    label: 'Agreed to flyers',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.agreedToFlyers' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: YES_NO_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.agreedToNewsletter',
    label: 'Agreed to newsletter',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.agreedToNewsletter' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: YES_NO_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.agreedToNewBlurb',
    label: 'Agreed to new blurb',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.agreedToNewBlurb' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: YES_NO_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.agreedToSocialMediaPost',
    label: 'Agreed to post social media post',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.agreedToSocialMediaPost' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: YES_NO_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.demoRun',
    label: 'Demo run',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.demoRun' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.flyersGivenToSchool',
    label: 'Flyers given to school',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.flyersGivenToSchool' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.flyersScannedByParents',
    label: 'Flyers scanned by parents',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.flyersScannedByParents' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.newsletter1Sent',
    label: 'Newsletter #1 sent',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.newsletter1Sent' },
    systemRequired: false,
    gates: ['marketing', 'parent_comms'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.newsletter2Sent',
    label: 'Newsletter #2 sent',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.newsletter2Sent' },
    systemRequired: false,
    gates: ['marketing', 'parent_comms'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.emailSentToCurrentParents',
    label: 'Email sent to current parents',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.emailSentToCurrentParents' },
    systemRequired: false,
    gates: ['marketing', 'parent_comms'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.emailSentToPastParents',
    label: 'Email sent to past parents',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.emailSentToPastParents' },
    systemRequired: false,
    gates: ['marketing', 'parent_comms'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.pastDeliverablesSentToDirectorAndMarketing',
    label: 'Past deliverables sent to director and marketing person',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.pastDeliverablesSentToDirectorAndMarketing' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.newBlurbsSentToDirector',
    label: 'New blurbs sent to director',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.newBlurbsSentToDirector' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.marketingLogistics.socialMediaPostSentToMarketingAndDirector',
    label: 'Social media post sent to marketing person and director',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'marketingLogistics.socialMediaPostSentToMarketingAndDirector' },
    systemRequired: false,
    gates: ['marketing'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: MARKETING_LOGISTICS_OUTCOME_OPTIONS,
  },
  {
    id: 'squad.proratedAllowed',
    label: 'Prorated programs / join partway allowed?',
    scope: 'squad',
    type: 'boolean',
    storage: { kind: 'squads', fieldPath: 'proratedAllowed' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'squad.delayProgramAWeekAllowed',
    label: 'Delay program a week allowed?',
    scope: 'squad',
    type: 'boolean',
    storage: { kind: 'squads', fieldPath: 'delayProgramAWeekAllowed' },
    systemRequired: false,
    gates: ['scheduling', 'teacher_assignment'],
    completeness: { kind: 'exists' },
    helpText: 'If enrollment is below minimum, can we delay start by one week?',
  },
  {
    id: 'squad.techTestedOnSite',
    label: 'Tech tested on-site',
    scope: 'squad',
    type: 'boolean',
    storage: { kind: 'squads', fieldPath: 'techTestedOnSite' },
    systemRequired: false,
    gates: ['scheduling', 'teacher_assignment'],
    completeness: { kind: 'exists' },
    helpText: 'Confirm WiFi + projector/TV + devices are tested on-site before first day.',
  },
  {
    id: 'squad.firstDayTeacherPacket',
    label: 'First-day teacher packet sent',
    scope: 'squad',
    type: 'text',
    storage: { kind: 'squads', fieldPath: 'firstDayTeacherPacket' },
    systemRequired: false,
    gates: ['teacher_assignment', 'other'],
    completeness: { kind: 'custom', note: 'Complete when packet has been sent (sentAt set).' },
    helpText: 'Generate, preview/edit, and send the “OG First Day Standard Operating Procedure” packet to the lead teacher.',
  },
  // --------------------
  // Halfway review (squad)
  // --------------------
  {
    id: 'squad.halfway.engagementScore',
    label: 'Halfway: Engagement score (1–5)',
    scope: 'squad',
    type: 'number',
    storage: { kind: 'squads', fieldPath: 'halfway.engagementScore' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'minNumber', min: 1 },
    helpText: '1 (low) → 5 (high).',
  },
  {
    id: 'squad.halfway.attendanceTrend',
    label: 'Halfway: Attendance trend',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'halfway.attendanceTrend' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'up', label: 'Up' },
      { id: 'flat', label: 'Flat' },
      { id: 'down', label: 'Down' },
    ],
  },
  {
    id: 'squad.halfway.pacingStatus',
    label: 'Halfway: Curriculum pacing status',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'halfway.pacingStatus' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'behind', label: 'Behind' },
      { id: 'on_track', label: 'On-track' },
      { id: 'ahead', label: 'Ahead' },
    ],
  },
  {
    id: 'squad.halfway.teacherPerformanceScore',
    label: 'Halfway: Teacher performance score (1–5)',
    scope: 'squad',
    type: 'number',
    storage: { kind: 'squads', fieldPath: 'halfway.teacherPerformanceScore' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'minNumber', min: 1 },
    helpText: '1 (needs improvement) → 5 (excellent).',
  },
  {
    id: 'squad.halfway.techIssueStatus',
    label: 'Halfway: Tech issue status',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'halfway.techIssueStatus' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'none', label: 'None' },
      { id: 'minor', label: 'Minor' },
      { id: 'blocking', label: 'Blocking' },
    ],
  },
  {
    id: 'squad.halfway.schoolSatisfactionScore',
    label: 'Halfway: School satisfaction score (1–5)',
    scope: 'squad',
    type: 'number',
    storage: { kind: 'squads', fieldPath: 'halfway.schoolSatisfactionScore' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'minNumber', min: 1 },
    helpText: '1 (unhappy) → 5 (very happy).',
  },
  {
    id: 'squad.halfway.renewalLikelihood',
    label: 'Halfway: Renewal likelihood',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'halfway.renewalLikelihood' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'yes', label: 'Yes' },
      { id: 'maybe', label: 'Maybe' },
      { id: 'no', label: 'No' },
    ],
  },
  {
    id: 'squad.enrolledKidNames',
    label: 'Names of kids enrolled',
    scope: 'squad',
    type: 'string_list',
    storage: { kind: 'squads', fieldPath: 'enrolledKidNames' },
    systemRequired: false,
    gates: ['invoicing', 'parent_comms'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'For school-paid (enrollment-based) squads: paste one student name per line (from the school roster).',
  },
  {
    id: 'squad.enrolledParentNamesAndEmails',
    label: 'List of parent emails & names',
    scope: 'squad',
    type: 'string_list',
    storage: { kind: 'squads', fieldPath: 'enrolledParentNamesAndEmails' },
    systemRequired: false,
    gates: ['invoicing', 'parent_comms'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'For school-paid (enrollment-based) squads: paste one entry per line, e.g. "Jane Doe — jane@example.com".',
  },
  {
    id: 'squad.studentRoster',
    label: 'Student roster (linked user accounts)',
    scope: 'squad',
    type: 'string_list',
    storage: { kind: 'squads', fieldPath: 'studentIds' },
    systemRequired: false,
    gates: ['parent_comms', 'other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Manage which student user accounts are linked to this squad (adds/removes IDs in studentIds).',
  },
  {
    id: 'squad.curriculumId',
    label: 'Curriculum offering',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'curriculumId' },
    systemRequired: false,
    gates: ['marketing', 'other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'music', label: 'Music' },
      { id: 'musicwinter2026', label: 'Music Winter 2026' },
      { id: 'ai', label: 'AI' },
      { id: 'ai-fall-2025', label: 'AI (Fall 2025)' },
      { id: 'ai-2026-create-worlds', label: 'AI (Create Worlds 2026)' },
      { id: 'entrepreneurship-2026', label: 'Entrepreneurship 2026' },
      { id: 'movie-making-2026', label: 'Movie Making 2026' },
      { id: 'game-design-2026', label: 'Game Design 2026' },
      { id: 'intro-ai-kids-2026', label: 'Intro to AI for Kids 2026' },
      { id: 'intro-ai-adults-2026', label: 'Intro to AI for Adults 2026' },
    ],
  },
  {
    id: 'squad.revSplitPercent',
    label: 'Rev split %',
    scope: 'squad',
    type: 'number',
    storage: { kind: 'squads', fieldPath: 'revSplitPercent' },
    systemRequired: false,
    gates: ['invoicing'],
    completeness: { kind: 'minNumber', min: 0 },
    helpText: 'Percent, e.g. 20 for 20%.',
  },
  {
    id: 'squad.minEnroll',
    label: 'Minimum enrollment',
    scope: 'squad',
    type: 'number',
    storage: { kind: 'squads', fieldPath: 'minEnroll' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'minNumber', min: 1 },
  },
  {
    id: 'squad.minEnrollDropDeadDate',
    label: 'Drop-dead date to cancel if min not met',
    scope: 'squad',
    type: 'date',
    storage: { kind: 'squads', fieldPath: 'minEnrollDropDeadDate' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.minEnrollOverrideBy',
    label: 'Who decides to override min?',
    scope: 'squad',
    type: 'text',
    storage: { kind: 'squads', fieldPath: 'minEnrollOverrideBy' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.commitmentConfirmation',
    label: 'Commitment confirmation (verbal/written)',
    scope: 'squad',
    type: 'text',
    storage: { kind: 'squads', fieldPath: 'commitmentConfirmation' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Example: "Written yes via email from decision maker on 2026-01-31"',
  },
  {
    id: 'squad.combineGradesAllowed',
    label: 'Allowed to combine groups/grades if needed?',
    scope: 'squad',
    type: 'boolean',
    storage: { kind: 'squads', fieldPath: 'combineGradesAllowed' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'squad.capacity',
    label: 'Capacity (max enrollment)',
    scope: 'squad',
    type: 'number',
    storage: { kind: 'squads', fieldPath: 'capacity' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'minNumber', min: 1 },
  },
  {
    id: 'squad.startGrade',
    label: 'Grade range (start)',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'startGrade' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'PK', label: 'Pre-K (PK)' },
      { id: 'K', label: 'Kindergarten (K)' },
      { id: '1', label: '1' },
      { id: '2', label: '2' },
      { id: '3', label: '3' },
      { id: '4', label: '4' },
      { id: '5', label: '5' },
      { id: '6', label: '6' },
      { id: '7', label: '7' },
      { id: '8', label: '8' },
      { id: '9', label: '9' },
      { id: '10', label: '10' },
      { id: '11', label: '11' },
      { id: '12', label: '12' },
      { id: 'adult', label: 'Adult' },
    ],
  },
  {
    id: 'squad.endGrade',
    label: 'Grade range (end)',
    scope: 'squad',
    type: 'enum',
    storage: { kind: 'squads', fieldPath: 'endGrade' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
    enumOptions: [
      { id: 'PK', label: 'Pre-K (PK)' },
      { id: 'K', label: 'Kindergarten (K)' },
      { id: '1', label: '1' },
      { id: '2', label: '2' },
      { id: '3', label: '3' },
      { id: '4', label: '4' },
      { id: '5', label: '5' },
      { id: '6', label: '6' },
      { id: '7', label: '7' },
      { id: '8', label: '8' },
      { id: '9', label: '9' },
      { id: '10', label: '10' },
      { id: '11', label: '11' },
      { id: '12', label: '12' },
      { id: 'adult', label: 'Adult' },
    ],
  },
  {
    id: 'squad.classroomName',
    label: 'Classroom name',
    scope: 'squad',
    type: 'text',
    storage: { kind: 'squads', fieldPath: 'classroomName' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.hasProjectorOrTv',
    label: 'Projector or TV available?',
    scope: 'squad',
    type: 'boolean',
    storage: { kind: 'squads', fieldPath: 'hasProjectorOrTv' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'squad.projectorConnectionInstructions',
    label: 'How do we connect to the projector/TV?',
    scope: 'squad',
    type: 'text',
    storage: { kind: 'squads', fieldPath: 'projectorConnectionInstructions' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.devicesOnSite',
    label: 'Devices on site?',
    scope: 'squad',
    type: 'boolean',
    storage: { kind: 'squads', fieldPath: 'devicesOnSite' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'squad.scheduleConfirmByDate',
    label: 'Day planned for THEM to confirm schedule',
    scope: 'squad',
    type: 'date',
    storage: { kind: 'squads', fieldPath: 'scheduleConfirmByDate' },
    systemRequired: false,
    gates: ['scheduling'],
    completeness: { kind: 'nonEmpty' },
  },
  {
    id: 'squad.serviceAgreementSigned',
    label: 'Service agreement sent and signed',
    scope: 'squad',
    type: 'boolean',
    storage: { kind: 'squads', fieldPath: 'serviceAgreementSigned' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'squad.plannedTeacherAvailabilityConfirmed',
    label: 'Planned teacher availability confirmed',
    scope: 'squad',
    type: 'boolean',
    storage: { kind: 'squads', fieldPath: 'plannedTeacherAvailabilityConfirmed' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'exists' },
    helpText: 'Confirm the planned lead teacher is available for this squad schedule.',
  },
  {
    id: 'squad.plannedTeacherOvertureTrainingCompleted',
    label: 'Planned teacher completed Overture Teaching Certification',
    scope: 'squad',
    type: 'boolean',
    storage: { kind: 'squads', fieldPath: 'plannedTeacherOvertureTrainingCompleted' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'exists' },
    helpText: 'Mark once the planned lead teacher has completed the required Overture Teaching Certification.',
  },
  {
    id: 'squad.leadTeacherId',
    label: 'Lead teacher assigned',
    scope: 'squad',
    type: 'text',
    storage: { kind: 'squads', fieldPath: 'leadTeacherId' },
    systemRequired: false,
    gates: ['teacher_assignment'],
    completeness: { kind: 'nonEmpty' },
    helpText: 'Admin UI currently uses `regularTeacher` for lead teacher; this is the long-term canonical field.',
  },

  // ---------------
  // Derived logistics
  // ---------------
  {
    id: 'derived.schoolDecisionMakerExists',
    label: 'Decision maker',
    scope: 'derived',
    type: 'person',
    storage: { kind: 'derived', ruleId: 'schoolDecisionMakerExists' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'derived.schoolAfterschoolDirectorExists',
    label: 'Afterschool director',
    scope: 'derived',
    type: 'person',
    storage: { kind: 'derived', ruleId: 'schoolAfterschoolDirectorExists' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'derived.schoolPrimaryContactExists',
    label: 'Primary school contact',
    scope: 'derived',
    type: 'person',
    storage: { kind: 'derived', ruleId: 'schoolPrimaryContactExists' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'derived.schoolTeacherChampionExists',
    label: 'Teacher champion',
    scope: 'derived',
    type: 'person',
    storage: { kind: 'derived', ruleId: 'schoolTeacherChampionExists' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'derived.enrollmentMeetsMin',
    label: 'Enrollment meets minimum',
    scope: 'derived',
    type: 'boolean',
    storage: { kind: 'derived', ruleId: 'enrollmentMeetsMin' },
    systemRequired: false,
    gates: ['teacher_assignment', 'other'],
    completeness: { kind: 'exists' },
    helpText: 'True when current enrollment is at least the squad minimum enrollment.',
  },
  {
    id: 'derived.squadHalfwayStatus',
    label: 'Overall halfway status',
    scope: 'derived',
    type: 'enum',
    storage: { kind: 'derived', ruleId: 'squadHalfwayStatus' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
    enumOptions: [
      { id: 'green', label: 'Green' },
      { id: 'yellow', label: 'Yellow' },
      { id: 'red', label: 'Red' },
    ],
    helpText: 'Derived from the halfway review fields (read-only).',
  },
  {
    id: 'derived.squadHalfwayNeedsIntervention',
    label: 'Needs intervention',
    scope: 'derived',
    type: 'boolean',
    storage: { kind: 'derived', ruleId: 'squadHalfwayNeedsIntervention' },
    systemRequired: false,
    gates: ['other'],
    completeness: { kind: 'exists' },
    helpText: 'Derived from overall halfway status (read-only).',
  },
  {
    id: 'derived.billingProfileExists',
    label: 'Billing profile exists',
    scope: 'derived',
    type: 'boolean',
    storage: { kind: 'derived', ruleId: 'billingProfileExists' },
    systemRequired: true,
    gates: ['invoicing'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'derived.approvedBillingRecipientExists',
    label: 'Authorized billing contact exists',
    scope: 'derived',
    type: 'boolean',
    storage: { kind: 'derived', ruleId: 'approvedBillingRecipientExists' },
    systemRequired: true,
    gates: ['invoicing'],
    completeness: { kind: 'exists' },
  },
  {
    id: 'derived.financeContactExists',
    label: 'Finance contact',
    scope: 'derived',
    type: 'person',
    storage: { kind: 'derived', ruleId: 'financeContactExists' },
    systemRequired: false,
    gates: ['invoicing', 'other'],
    completeness: { kind: 'exists' },
  },
] as const;

export const OPS_LOGISTICS_BY_ID: Readonly<Record<string, OpsLogisticDefinition>> =
  Object.freeze(
    OPS_LOGISTICS_CATALOG.reduce((acc, d) => {
      acc[d.id] = d;
      return acc;
    }, {} as Record<string, OpsLogisticDefinition>)
  );
